import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const STEPS = [
  { number: 1, label: 'Your details' },
  { number: 2, label: 'The property' },
  { number: 3, label: 'The court' },
  { number: 4, label: 'Availability' },
  { number: 5, label: 'Photos & content' },
  { number: 6, label: 'Final details' },
];

interface StepIndicatorProps {
  current: number;
}

export default function StepIndicator({ current }: StepIndicatorProps) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const handleSaveExit = () => {
    setSaving(true);
    // Progress is already persisted — just give a brief visual confirmation
    setTimeout(() => {
      navigate('/');
    }, 900);
  };
  const progressPct = ((current - 1) / (STEPS.length - 1)) * 100;

  return (
    <header className="w-full bg-[#f2ece2] px-6 md:px-12 xl:px-20 pt-8 pb-0">
      {/* Logo row */}
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="cursor-pointer">
          <span className="font-serif text-[#b6862d] text-2xl leading-tight tracking-tight">
            Padel<span className="text-[#141413]">Villas</span>
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {/* Mobile step counter */}
          <span className="text-[#77776f] text-xs tracking-widest uppercase font-sans md:hidden">
            Step {current} of 6
          </span>

          {/* Save & exit button */}
          <button
            type="button"
            onClick={handleSaveExit}
            disabled={saving}
            className={`flex items-center gap-2 text-xs font-sans px-4 py-2 rounded-lg border transition-all duration-300 cursor-pointer whitespace-nowrap ${
              saving
                ? 'bg-[#234012] border-[#234012] text-[#fcfbf7]'
                : 'bg-transparent border-[#d0c9be] text-[#77776f] hover:border-[#b6862d] hover:text-[#3a3a34]'
            }`}
          >
            {saving ? (
              <>
                <i className="ri-check-line text-[#b6862d]" />
                <span>Saved</span>
              </>
            ) : (
              <>
                <i className="ri-save-3-line" />
                <span>Save &amp; exit</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="hidden md:block w-full max-w-3xl mx-auto">
        <div className="relative flex items-start justify-between">
          {/* Track line */}
          <div className="absolute top-4 left-0 right-0 h-px bg-[#e0d9ce]" />
          {/* Filled line */}
          <div
            className="absolute top-4 left-0 h-px bg-[#b6862d] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />

          {STEPS.map((step) => {
            const isDone = step.number < current;
            const isActive = step.number === current;
            return (
              <div key={step.number} className="relative flex flex-col items-center" style={{ width: '16.666%' }}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium font-sans z-10 transition-all duration-300 border-2 ${
                    isDone
                      ? 'bg-[#234012] border-[#234012] text-[#fcfbf7]'
                      : isActive
                      ? 'bg-[#b6862d] border-[#b6862d] text-[#fcfbf7]'
                      : 'bg-[#f2ece2] border-[#e0d9ce] text-[#77776f]'
                  }`}
                >
                  {isDone ? <i className="ri-check-line text-xs" /> : step.number}
                </div>
                <span
                  className={`mt-2 text-xs font-sans text-center leading-tight transition-colors duration-300 ${
                    isActive ? 'text-[#b6862d] font-medium' : isDone ? 'text-[#3a3a34]' : 'text-[#77776f]'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile slim bar */}
      <div className="md:hidden w-full h-1 bg-[#e0d9ce] rounded-full mt-2">
        <div
          className="h-1 bg-[#b6862d] rounded-full transition-all duration-500"
          style={{ width: `${(current / 6) * 100}%` }}
        />
      </div>

      <div className="w-full max-w-3xl mx-auto">
        <div className="border-t border-[#e0d9ce] mt-8" />
      </div>
    </header>
  );
}
